<template>
  <div class="my-stores">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <div class="header-info">
            <h1 class="page-title">
              <i class="el-icon-s-shop"></i>
              我的门店
            </h1>
            <p class="page-subtitle">管理我申请的门店信息</p>
          </div>
        </div>
        <div class="header-actions">
          <el-button size="medium" @click="loadMyStores">
            <i class="el-icon-refresh"></i> 刷新数据
          </el-button>
          <el-button type="primary" size="medium" @click="handleNewApplication">
            <i class="el-icon-plus"></i> 申请门店
          </el-button>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 筛选区域 -->
      <div class="filter-section">
        <div class="filter-card">
          <el-form :model="queryForm" :inline="true" class="filter-form">
            <el-form-item label="门店状态">
              <el-select v-model="queryForm.status" placeholder="全部状态" clearable>
                <el-option label="正常运营" :value="1">
                  <span style="color: #67c23a">
                    <i class="el-icon-success"></i> 正常运营
                  </span>
                </el-option>
                <el-option label="已冻结" :value="3">
                  <span style="color: #f56c6c">
                    <i class="el-icon-warning"></i> 已冻结
                  </span>
                </el-option>
              </el-select>
            </el-form-item>
            
            <el-form-item>
              <el-button type="primary" @click="handleSearch" :loading="loading">
                <i class="el-icon-search"></i> 搜索
              </el-button>
              <el-button @click="handleReset">
                <i class="el-icon-refresh-left"></i> 重置
              </el-button>
            </el-form-item>
          </el-form>
        </div>
      </div>

      <!-- 门店列表 -->
      <div class="stores-section">
        <div v-if="loading" class="loading-container">
          <el-skeleton :rows="3" animated />
        </div>
        
        <div v-else-if="storeList.length === 0" class="empty-container">
          <div class="empty-content">
            <i class="el-icon-s-shop"></i>
            <p>暂无门店信息</p>
            <el-button type="primary" @click="handleNewApplication">
              <i class="el-icon-plus"></i> 立即申请
            </el-button>
          </div>
        </div>

        <div v-else class="store-cards">
          <div 
            v-for="store in storeList" 
            :key="store.id" 
            class="store-card"
            :class="getCardClass(store.status)"
          >
            <div class="card-header">
              <div class="store-info">
                <h3 class="store-title">{{ store.store_name }}</h3>
                <div class="store-meta">
                  <span class="store-id">门店编号：{{ store.id }}</span>
                  <span class="store-type">
                    {{ store.store_type === 1 ? '实体门店' : '代理商' }}
                  </span>
                  <span class="certificate-no" v-if="store.certificate_no">
                    证书编号：{{ store.certificate_no }}
                  </span>
                </div>
              </div>
              <div class="status-badge" :class="getStatusClass(store.status)">
                <i :class="getStatusIcon(store.status)"></i>
                {{ getStatusText(store.status) }}
              </div>
            </div>

            <div class="card-content">
              <div class="info-section">
                <div class="info-row">
                  <div class="info-item">
                    <label><i class="el-icon-user"></i> 负责人：</label>
                    <span>{{ store.contact_person }}</span>
                  </div>
                  <div class="info-item">
                    <label><i class="el-icon-phone"></i> 联系电话：</label>
                    <span>{{ store.contact_phone }}</span>
                  </div>
                </div>
                
                <div class="info-row">
                  <div class="info-item full-width">
                    <label><i class="el-icon-location"></i> 门店地址：</label>
                    <span>{{ store.address }}</span>
                  </div>
                </div>

                <div class="info-row">
                  <div class="info-item">
                    <label><i class="el-icon-time"></i> 创建时间：</label>
                    <span>{{ store.created_at | formatDate }}</span>
                  </div>
                </div>
              </div>

              <!-- 推广二维码 -->
              <div v-if="store.qr_code" class="qr-section">
                <div class="qr-header">
                  <label><i class="el-icon-qrcode"></i> 推广二维码：</label>
                </div>
                <div class="qr-content">
                  <div class="qr-image-container">
                    <img :src="store.qr_code" class="qr-image" alt="推广二维码" @click="previewQRCode(store.qr_code)">
                    <div class="qr-actions">
                      <el-button size="mini" @click="previewQRCode(store.qr_code)">
                        <i class="el-icon-view"></i> 预览
                      </el-button>
                      <el-button size="mini" @click="downloadQRCode(store.qr_code, store.store_name)">
                        <i class="el-icon-download"></i> 下载
                      </el-button>
                    </div>
                  </div>
                  <div class="qr-description">
                    <p>扫描二维码可绑定门店</p>
                    <p>分享给客户进行门店绑定</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="card-actions">
              <el-button size="small" @click="handleViewDetail(store)">
                <i class="el-icon-view"></i> 查看详情
              </el-button>
              
              <el-button size="small" type="primary" @click="handleEdit(store)">
                <i class="el-icon-edit"></i> 编辑信息
              </el-button>
              
              <el-button 
                size="small" 
                type="warning" 
                @click="handleRegenerateQR(store)"
                v-if="store.status === 1"
              >
                <i class="el-icon-refresh"></i> 重新生成二维码
              </el-button>

              <el-dropdown @command="handleCommand($event, store)" trigger="click">
                <el-button size="small">
                  更多 <i class="el-icon-arrow-down"></i>
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="certificate">查看证书</el-dropdown-item>
                  <el-dropdown-item command="products">门店商品</el-dropdown-item>
                  <el-dropdown-item command="users">绑定用户</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <div v-if="total > 0" class="pagination-section">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="queryForm.page"
            :page-sizes="[5, 10, 20]"
            :page-size="queryForm.page_size"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
            background
          >
          </el-pagination>
        </div>
      </div>
    </div>

    <!-- 编辑门店信息对话框 -->
    <el-dialog
      :visible.sync="editVisible"
      width="60%"
      title="编辑门店信息"
      :before-close="handleCloseEdit"
    >
      <el-form
        v-if="currentStore"
        ref="editForm"
        :model="editForm"
        :rules="editRules"
        label-width="120px"
      >
        <el-form-item label="负责人" prop="contact_person">
          <el-input v-model="editForm.contact_person" placeholder="请输入负责人姓名" />
        </el-form-item>
        
        <el-form-item label="联系电话" prop="contact_phone">
          <el-input v-model="editForm.contact_phone" placeholder="请输入联系电话" />
        </el-form-item>
        
        <el-form-item label="详细地址" prop="address">
          <el-input v-model="editForm.address" placeholder="请输入详细地址" />
        </el-form-item>
        
        <el-form-item label="所在省份" prop="province">
          <el-input v-model="editForm.province" placeholder="请输入省份" />
        </el-form-item>
        
        <el-form-item label="所在城市" prop="city">
          <el-input v-model="editForm.city" placeholder="请输入城市" />
        </el-form-item>
        
        <el-form-item label="所在区县" prop="district">
          <el-input v-model="editForm.district" placeholder="请输入区县" />
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="handleCloseEdit">取消</el-button>
        <el-button type="primary" @click="handleSaveEdit" :loading="saveLoading">保存</el-button>
      </div>
    </el-dialog>

    <!-- 二维码预览对话框 -->
    <el-dialog :visible.sync="qrPreviewVisible" width="40%" center title="推广二维码">
      <div class="qr-preview-content">
        <img :src="previewQRUrl" class="qr-preview-image" alt="推广二维码">
        <div class="qr-preview-actions">
          <el-button type="primary" @click="downloadQRCode(previewQRUrl, currentStoreName)">
            <i class="el-icon-download"></i> 下载二维码
          </el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getMyOwnedStores, updateStoreInfo, generateStoreQRCode } from '@/api/store'

export default {
  name: 'MyStores',
  
  data() {
    return {
      loading: false,
      saveLoading: false,
      storeList: [],
      total: 0,
      queryForm: {
        page: 1,
        page_size: 10,
        status: null
      },
      editVisible: false,
      currentStore: null,
      editForm: {
        contact_person: '',
        contact_phone: '',
        address: '',
        province: '',
        city: '',
        district: ''
      },
      editRules: {
        contact_person: [
          { required: true, message: '请输入负责人姓名', trigger: 'blur' }
        ],
        contact_phone: [
          { required: true, message: '请输入联系电话', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
        ],
        address: [
          { required: true, message: '请输入详细地址', trigger: 'blur' }
        ]
      },
      qrPreviewVisible: false,
      previewQRUrl: '',
      currentStoreName: ''
    }
  },
  
  mounted() {
    this.loadMyStores()
  },
  
  methods: {
    // 加载我的门店列表
    async loadMyStores() {
      this.loading = true
      try {
        const response = await getMyOwnedStores(this.queryForm)
        this.storeList = response.data.list || []
        this.total = response.data.pagination?.total || 0
      } catch (error) {
        console.error('获取门店列表失败:', error)
        this.$message.error('获取门店列表失败')
      } finally {
        this.loading = false
      }
    },
    
    // 搜索
    handleSearch() {
      this.queryForm.page = 1
      this.loadMyStores()
    },
    
    // 重置
    handleReset() {
      this.queryForm = {
        page: 1,
        page_size: 10,
        status: null
      }
      this.loadMyStores()
    },
    
    // 分页大小改变
    handleSizeChange(val) {
      this.queryForm.page_size = val
      this.queryForm.page = 1
      this.loadMyStores()
    },
    
    // 当前页改变
    handleCurrentChange(val) {
      this.queryForm.page = val
      this.loadMyStores()
    },
    
    // 新申请
    handleNewApplication() {
      this.$router.push('/store-application')
    },
    
    // 查看详情
    handleViewDetail(store) {
      this.$router.push(`/my-stores/${store.id}`)
    },
    
    // 编辑门店信息
    handleEdit(store) {
      this.currentStore = store
      this.editForm = {
        contact_person: store.contact_person || '',
        contact_phone: store.contact_phone || '',
        address: store.address || '',
        province: store.province || '',
        city: store.city || '',
        district: store.district || ''
      }
      this.editVisible = true
    },
    
    // 关闭编辑对话框
    handleCloseEdit() {
      this.editVisible = false
      this.currentStore = null
      this.editForm = {
        contact_person: '',
        contact_phone: '',
        address: '',
        province: '',
        city: '',
        district: ''
      }
    },
    
    // 保存编辑
    async handleSaveEdit() {
      try {
        await this.$refs.editForm.validate()
        
        this.saveLoading = true
        
        await updateStoreInfo(this.currentStore.id, this.editForm)
        
        this.$message.success('门店信息更新成功')
        this.handleCloseEdit()
        this.loadMyStores()
        
      } catch (error) {
        console.error('更新门店信息失败:', error)
        if (error !== 'cancel') {
          this.$message.error('更新门店信息失败')
        }
      } finally {
        this.saveLoading = false
      }
    },
    
    // 重新生成二维码
    async handleRegenerateQR(store) {
      try {
        await this.$confirm(`确定要重新生成"${store.store_name}"的推广二维码吗？原二维码将失效`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const response = await generateStoreQRCode(store.id)
        store.qr_code = response.data.qr_code_url
        
        this.$message.success('二维码重新生成成功')
        
      } catch (error) {
        if (error !== 'cancel') {
          console.error('重新生成二维码失败:', error)
          this.$message.error('重新生成二维码失败')
        }
      }
    },
    
    // 预览二维码
    previewQRCode(qrUrl) {
      this.previewQRUrl = qrUrl
      this.qrPreviewVisible = true
    },
    
    // 下载二维码
    downloadQRCode(qrUrl, storeName) {
      const link = document.createElement('a')
      link.href = qrUrl
      link.download = `${storeName}_推广二维码.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      this.$message.success('二维码下载成功')
    },
    
    // 处理下拉菜单命令
    handleCommand(command, store) {
      switch (command) {
        case 'certificate':
          if (store.certificate_url) {
            window.open(store.certificate_url)
          } else {
            this.$message.info('暂无证书文件')
          }
          break
        case 'products':
          this.$router.push(`/my-stores/${store.id}/products`)
          break
        case 'users':
          this.$router.push(`/my-stores/${store.id}/users`)
          break
      }
    },
    
    // 获取卡片样式类
    getCardClass(status) {
      switch (status) {
        case 1: return 'status-active'
        case 3: return 'status-frozen'
        default: return ''
      }
    },
    
    // 获取状态样式类
    getStatusClass(status) {
      switch (status) {
        case 1: return 'status-active'
        case 3: return 'status-frozen'
        default: return 'status-unknown'
      }
    },
    
    // 获取状态图标
    getStatusIcon(status) {
      switch (status) {
        case 1: return 'el-icon-success'
        case 3: return 'el-icon-warning'
        default: return 'el-icon-info'
      }
    },
    
    // 获取状态文本
    getStatusText(status) {
      switch (status) {
        case 1: return '正常运营'
        case 3: return '已冻结'
        default: return '未知状态'
      }
    }
  },
  
  filters: {
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('zh-CN')
    }
  }
}
</script>

<style scoped>
/* 页面基础样式 */
.my-stores {
  min-height: 100vh;
  background: #f5f7fa;
}

/* 页面头部 */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 40px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  flex: 1;
}

.header-info .page-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
}

.header-info .page-title i {
  font-size: 32px;
  color: white;
}

.header-info .page-subtitle {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
  line-height: 1.5;
  color: white;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.header-actions .el-button {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-weight: 500;
}

.header-actions .el-button--primary {
  background: rgba(255, 255, 255, 0.95);
  color: #667eea;
  border-color: transparent;
}

.header-actions .el-button:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.header-actions .el-button--primary:hover {
  background: white;
  color: #667eea;
}

/* 主内容区域 */
.main-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
}

/* 筛选区域 */
.filter-section {
  margin-bottom: 30px;
}

.filter-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
}

/* 空状态 */
.empty-container {
  background: white;
  border-radius: 16px;
  padding: 60px 20px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.empty-content i {
  font-size: 64px;
  color: #dcdfe6;
  margin-bottom: 16px;
}

.empty-content p {
  font-size: 16px;
  color: #909399;
  margin-bottom: 20px;
}

/* 门店卡片 */
.store-cards {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin-bottom: 30px;
}

.store-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  transition: all 0.3s ease;
  border-left: 4px solid #dcdfe6;
}

.store-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.store-card.status-active {
  border-left-color: #67c23a;
}

.store-card.status-frozen {
  border-left-color: #f56c6c;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}

.store-title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 8px 0;
}

.store-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 14px;
  color: #909399;
}

.status-badge {
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.status-badge.status-active {
  background: #f0f9ff;
  color: #67c23a;
}

.status-badge.status-frozen {
  background: #fef0f0;
  color: #f56c6c;
}

.card-content {
  margin-bottom: 24px;
}

.info-section {
  margin-bottom: 24px;
}

.info-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 12px;
}

.info-item {
  display: flex;
  align-items: center;
  font-size: 14px;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-item label {
  color: #606266;
  margin-right: 8px;
  font-weight: 500;
  min-width: 80px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.info-item label i {
  font-size: 16px;
}

.info-item span {
  color: #303133;
}

/* 二维码区域 */
.qr-section {
  background: #fafbfc;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #f0f2f5;
}

.qr-header {
  margin-bottom: 12px;
}

.qr-header label {
  font-size: 14px;
  font-weight: 600;
  color: #606266;
  display: flex;
  align-items: center;
  gap: 6px;
}

.qr-content {
  display: flex;
  gap: 16px;
  align-items: center;
}

.qr-image-container {
  text-align: center;
}

.qr-image {
  width: 80px;
  height: 80px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  margin-bottom: 8px;
}

.qr-image:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.qr-actions {
  display: flex;
  gap: 4px;
}

.qr-description {
  flex: 1;
  font-size: 13px;
  color: #909399;
  line-height: 1.6;
}

.qr-description p {
  margin: 0 0 4px 0;
}

.card-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid #f0f2f5;
  flex-wrap: wrap;
}

/* 二维码预览 */
.qr-preview-content {
  text-align: center;
}

.qr-preview-image {
  width: 200px;
  height: 200px;
  margin-bottom: 20px;
}

.qr-preview-actions {
  text-align: center;
}

/* 分页 */
.pagination-section {
  background: white;
  border-radius: 16px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-header {
    padding: 20px;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .header-actions {
    width: 100%;
  }
  
  .header-actions .el-button {
    flex: 1;
  }
  
  .main-content {
    padding: 16px 20px;
  }
  
  .store-meta {
    flex-direction: column;
    gap: 8px;
  }
  
  .info-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  
  .qr-content {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  
  .card-actions {
    flex-direction: column;
  }
  
  .card-actions .el-button {
    width: 100%;
  }
}
</style>
